import {noop} from 'lodash';
import React, {FC, ReactNode, useRef} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {InfoBubblePillVariantsEnum} from '../../../../../libs/shared/ui/src/components/tip/infoBubblePill';
import heroImage from '../../../../assets/images/sandboxTip.svg';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SandboxScenariosEnum} from '../../../../helpers/sandbox/sandboxScenarioHelpers';
import {getIsSandboxGeneralInboxTicketingEnabled} from '../../../../store/data/inboxes/inboxesSelectors';
import {navigateToNewSandboxDemo} from '../../../../store/data/teammates/teammatesActions';
import {useOnboardingModalState} from '../../../app/topBar/onboarding/onboardingContext';
import {EducationTip} from '../../../educationTips/educationTip';
import {OnboardingGuidesEnum} from '../../guide/onboardingGuideTypes';
import {useSandboxEndDemoHandler} from '../../sandbox/educationTips/sandboxEducationTipCoordinator';

/*
 * Props.
 */

interface SandboxDemoIntroEducationTipProps {
  children: ReactNode;
  isSandboxTeam: boolean;
}

/*
 * Styles.
 */

const StyledImg = styled.img`
  display: block;
  width: 380px;
  height: 142px;
`;

/*
 * Component.
 */

export const SandboxDemoIntroEducationTip: FC<SandboxDemoIntroEducationTipProps> = (props) => {
  const {children, isSandboxTeam} = props;

  const {trackDataEvent} = useDataEventTracking();
  const dispatch = useAppDispatch();
  const {onboardingGuide, setOnboardingGuide} = useOnboardingModalState();
  const onEndDemo = useSandboxEndDemoHandler();
  const anchor = useRef<HTMLDivElement>(null);

  const hasTicketingSupportInbox = useAppSelector(getIsSandboxGeneralInboxTicketingEnabled);

  const onStartClick = async () => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.DEMO_WELCOME_START, {});

    await dispatch(
      navigateToNewSandboxDemo({
        scenario: hasTicketingSupportInbox
          ? SandboxScenariosEnum.SCENARIO5_ACT1
          : SandboxScenariosEnum.SCENARIO3_ACT1,
        step: hasTicketingSupportInbox
          ? 'start-scenario-5-conversation-1'
          : 'start-scenario-3-conversation-1',
      }),
    );

    setOnboardingGuide(undefined);
  };

  const onSkipClick = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.DEMO_WELCOME_SKIP, {});
    setOnboardingGuide(undefined);
    onEndDemo();
  };

  return (
    <div ref={anchor}>
      <EducationTip
        anchor={anchor}
        position="right-start"
        isInteractive
        isVisible={isSandboxTeam && onboardingGuide === OnboardingGuidesEnum.SANDBOX_DEMO_INTRO}
        onRequestClose={noop}
        hero={<StyledImg src={heroImage} alt="" />}
        pillType={InfoBubblePillVariantsEnum.EDUCATION}
        title={
          <Trans id="sandbox-demo-intro-education-tip-title">
            Learn how to use Front faster than sending one email
          </Trans>
        }
        primaryActionLabel={<Trans id="sandbox-demo-intro-education-tip-start">Show me</Trans>}
        onPrimaryClick={onStartClick}
        secondaryAction={{
          label: <Trans id="sandbox-demo-intro-education-tip-skip">No thanks</Trans>,
          onClick: onSkipClick,
        }}
        dataObjectName={OnboardingDataEventObject.DEMO_WELCOME}
        overlayOffset={0}
      >
        <Trans id="sandbox-demo-intro-education-tip-description">
          Give us two minutes, and we&apos;ll give you the tools you need to set your team up for success in
          Front.
        </Trans>
      </EducationTip>
      {children}
    </div>
  );
};
